import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import './index.css'
import App from './App.tsx'
import { ErrorBoundary } from './ErrorBoundary'

// Load analytics/Sentry only after explicit user interaction to keep landing bundle light
if (import.meta.env.PROD) {
  const loadAnalytics = () => import('./lib/analytics')
  const triggerAnalytics = () => {
    loadAnalytics()
    ;['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach((ev) =>
      window.removeEventListener(ev, triggerAnalytics, { once: true } as any)
    )
  }
  // Only hook interactions on non-public marketing routes
  const isPublic =
    window.location.pathname === '/' ||
    window.location.pathname.startsWith('/privacy') ||
    window.location.pathname.startsWith('/terms') ||
    window.location.pathname.startsWith('/updates')
  if (!isPublic) {
    window.addEventListener('load', () => {
      ;['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach((ev) =>
        window.addEventListener(ev, triggerAnalytics, { once: true, passive: true } as any)
      )
    })
  }
}

// Apply persisted theme BEFORE rendering so initial paint matches user preference
try {
  const storedTheme = window.localStorage.getItem('ember-theme');
  if (storedTheme === 'high-contrast') {
    document.documentElement.setAttribute('data-theme', 'high-contrast');
  } else {
    // Ensure we are on the default dark theme
    document.documentElement.removeAttribute('data-theme');
  }
} catch {
  // If localStorage is unavailable, silently fall back to default (dark) theme
}

// Build version indicator (helps verify cache updates)
const BUILD_ID = `${Date.now()}-${Math.random().toString(36).substring(7)}`;
console.log(`🔥 Ember App - Build: ${BUILD_ID}`);
console.log(`📊 Sentry Error Monitoring: Active`);
console.log(`🚀 Environment: ${import.meta.env.MODE}`);

// PWA debugging
console.log(`📱 PWA Mode: ${window.matchMedia('(display-mode: standalone)').matches ? 'Standalone' : 'Browser'}`);
console.log(`🌐 User Agent: ${navigator.userAgent}`);
console.log(`🔧 Service Worker: ${'serviceWorker' in navigator ? 'Supported' : 'Not Supported'}`);
console.log(`🌍 Online: ${navigator.onLine ? 'Yes' : 'No'}`);

// Network status monitoring
window.addEventListener('online', () => console.log('🌍 Network: Online'));
window.addEventListener('offline', () => console.log('🌍 Network: Offline'));

console.log('[MAIN] Starting React app...');
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
)
console.log('[MAIN] React app rendered');

// Service worker strategy:
// - Rely on VitePWA (Workbox) to auto-register `sw.js`.
// - Proactively unregister legacy `/service-worker.js` if it exists to prevent SW conflicts.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', async () => {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const reg of regs) {
        const scriptUrl = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL;
        if (scriptUrl && scriptUrl.endsWith('/service-worker.js')) {
          console.warn('[MAIN] Unregistering legacy service worker:', scriptUrl);
          await reg.unregister();
        }
      }
    } catch (e) {
      console.warn('[MAIN] Legacy SW cleanup skipped:', e);
    }
  });
} else {
  console.log('[MAIN] Service worker not supported or not in production');
}

// Register the PWA service worker eagerly on load to ensure install/update readiness
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    const doRegister = registerSW({
      immediate: true,
      onRegisteredSW(swUrl) {
        console.log('[MAIN] Service worker registered at', swUrl);
      },
      onRegisterError(error) {
        console.warn('[MAIN] Service worker registration failed', error);
      },
    });
    doRegister?.();
  });
}
